/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { concat, of } from 'rxjs';
import { catchError, map, toArray } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { BrowseNode, CopyMoveService, FolderInfo } from './copy-move.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { PromptDialogComponent } from '../shared/prompt-dialog.component';

type Operation = 'copy' | 'move';

const ROOT = '-root-';
const PAGE_SIZE = 50;

const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG'
};

/**
 * Copy / move workspace (UI-08).
 *
 * Three panes: the source folder's contents in the middle, the destination
 * browser on the right, and the source's own information on the left. The
 * operation runs one node at a time so a partial failure — a name collision, a
 * folder the user cannot write to — is reported per item instead of collapsing
 * the batch into a single result.
 */
@Component({
  selector: 'app-copy-move',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './copy-move.component.html',
  styleUrls: ['./copy-move.component.scss']
})
export class CopyMoveComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly service = inject(CopyMoveService);
  private readonly notification = inject(NotificationService);

  // ---- source pane --------------------------------------------------------
  readonly sourceNodes = signal<BrowseNode[]>([]);
  readonly sourceInfo = signal<FolderInfo | null>(null);
  readonly sourceTotal = signal(0);
  readonly sourceLoading = signal(true);
  readonly sourceFailed = signal('');
  sourceId = ROOT;

  // ---- destination pane ---------------------------------------------------
  readonly targetNodes = signal<BrowseNode[]>([]);
  readonly targetInfo = signal<FolderInfo | null>(null);
  readonly targetLoading = signal(false);
  targetId = ROOT;

  readonly selectedIds = signal<Set<string>>(new Set());
  readonly working = signal(false);
  operation: Operation = 'copy';

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly allSelected = computed(() => {
    const nodes = this.sourceNodes();
    return nodes.length > 0 && nodes.every((node) => this.selectedIds().has(node.id));
  });
  readonly someSelected = computed(() => this.selectedCount() > 0 && !this.allSelected());

  /** The destination must be a real folder the caller can write into. */
  readonly canPaste = computed(() => this.selectedCount() > 0 && !!this.targetInfo() && !this.working() && this.targetId !== this.sourceId);

  ngOnInit(): void {
    this.loadSource(ROOT);
    this.loadTarget(ROOT);
  }

  // ---- source -------------------------------------------------------------

  loadSource(folderId: string): void {
    this.sourceId = folderId;
    this.sourceLoading.set(true);
    this.sourceFailed.set('');
    this.selectedIds.set(new Set());

    this.service.children(folderId, 0, PAGE_SIZE).subscribe({
      next: ({ nodes, total }) => {
        this.sourceNodes.set(nodes);
        this.sourceTotal.set(total);
        this.sourceLoading.set(false);
      },
      error: (err) => {
        this.sourceLoading.set(false);
        this.sourceNodes.set([]);
        this.sourceFailed.set(this.describe(err, 'DMS.UI.E2BEAE'));
      }
    });

    this.service.info(folderId).subscribe({
      next: (info) => this.sourceInfo.set(info),
      error: () => this.sourceInfo.set(null)
    });
  }

  openSource(node: BrowseNode): void {
    if (node.isFolder) {
      this.loadSource(node.id);
    }
  }

  // ---- destination --------------------------------------------------------

  loadTarget(folderId: string): void {
    this.targetId = folderId;
    this.targetLoading.set(true);

    this.service.children(folderId, 0, PAGE_SIZE).subscribe({
      next: ({ nodes }) => {
        this.targetNodes.set(nodes.filter((node) => node.isFolder));
        this.targetLoading.set(false);
      },
      error: () => {
        this.targetNodes.set([]);
        this.targetLoading.set(false);
      }
    });

    this.service.info(folderId).subscribe({
      next: (info) => this.targetInfo.set(info),
      error: () => this.targetInfo.set(null)
    });
  }

  /** Steps the destination browser up to an ancestor from its breadcrumb. */
  targetUp(crumbId: string): void {
    this.loadTarget(crumbId);
  }

  createTargetFolder(): void {
    this.dialog
      .open(PromptDialogComponent, { data: { title: 'DMS.UI.569471', fields: [{ key: 'name', label: 'DMS.UI.569471', required: true }] } })
      .afterClosed()
      .subscribe((values?: Record<string, string>) => values?.['name'] && this.makeFolder(values['name']));
  }

  private makeFolder(name: string): void {
    this.service.createFolder(this.targetId, name).subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.UI.26E525', { p0: name }));
        this.loadTarget(this.targetId);
      },
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.D3C60A'))
    });
  }

  // ---- selection ----------------------------------------------------------

  isSelected(node: BrowseNode): boolean {
    return this.selectedIds().has(node.id);
  }

  toggle(node: BrowseNode): void {
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }
      return next;
    });
  }

  toggleAll(): void {
    this.selectedIds.set(this.allSelected() ? new Set() : new Set(this.sourceNodes().map((node) => node.id)));
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  setOperation(operation: Operation): void {
    this.operation = operation;
  }

  // ---- the operation ------------------------------------------------------

  paste(): void {
    const ids = this.selectedIds();
    const nodes = this.sourceNodes().filter((node) => ids.has(node.id));
    if (!nodes.length || !this.canPaste()) {
      return;
    }

    const target = this.targetId;
    const isCopy = this.operation === 'copy';
    const verb = this.translate.instant(isCopy ? 'DMS.UI.C480AD' : 'DMS.UI.3BBE19');
    this.working.set(true);

    concat(
      ...nodes.map((node) =>
        (isCopy ? this.service.copy(node.id, target) : this.service.move(node.id, target)).pipe(
          map(() => ({ name: node.name, reason: '' })),
          catchError((err) => of({ name: node.name, reason: this.describe(err, this.translate.instant('DMS.UI.3E081F', { p0: verb })) }))
        )
      )
    )
      .pipe(toArray())
      .subscribe((results) => {
        this.working.set(false);

        const failed = results.filter((r) => r.reason);
        const ok = results.length - failed.length;

        if (!failed.length) {
          this.notification.showInfo(this.translate.instant('DMS.UI.D62D6E', { p0: verb, p1: ok }));
        } else if (!ok) {
          this.notification.showError(this.translate.instant('DMS.UI.A7DB0E', { p0: verb, p1: failed[0].reason }));
        } else {
          this.notification.showWarning(
            this.translate.instant('DMS.UI.E47685', { p0: verb, p1: ok, p2: failed.length, p3: failed[0].name, p4: failed[0].reason })
          );
        }

        // a move changes both panes, so both are re-read from the repository
        this.loadSource(this.sourceId);
        this.loadTarget(target);
      });
  }

  // ---- view helpers -------------------------------------------------------

  label(value: string): string {
    return value ? DmsMetadataService.label(value) : '';
  }

  badge(node: BrowseNode): string {
    return node.isFolder ? '' : (BADGES[this.extension(node.name)] ?? 'FILE');
  }

  badgeClass(node: BrowseNode): string {
    return 'aca-cm__badge--' + (node.isFolder ? 'folder' : this.extension(node.name));
  }

  formatSize(node: BrowseNode): string {
    const bytes = node.sizeInBytes;
    if (node.isFolder || bytes === undefined || bytes === null) {
      return '—';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  targetPath(): string {
    const info = this.targetInfo();
    return info ? info.crumbs.map((crumb) => crumb.name).join(' / ') : '—';
  }

  trackById(_: number, node: BrowseNode): string {
    return node.id;
  }

  trackByCrumb(_: number, crumb: { id: string }): string {
    return crumb.id;
  }

  private extension(name: string): string {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  private describe(error: any, fallback: string): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant(fallback);
  }
}
